import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowPathIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ClockIcon,
  ComputerDesktopIcon,
  DocumentCheckIcon,
  EyeIcon,
  GlobeAltIcon,
  MapPinIcon,
  ShieldCheckIcon,
  SignalIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import {
  PageCta,
  PageHero,
  SectionHeading,
} from '../components/page-templates/SitePageTemplates';

interface DeliverableItem {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
}

interface CaseStudy {
  id: string;
  title: string;
  client: string;
  challenge: string;
  deliverables: DeliverableItem[];
  results: string[];
  accentColor: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const caseStudies: CaseStudy[] = [
  {
    id: 'enterprise-rollout',
    title: '42-Country Enterprise ICT Rollout',
    client: 'A global ICT management provider',
    challenge:
      'A multi-country rollout across APAC and MEA with strict SLAs tied to service revenue and a wide range of import environments.',
    deliverables: [
      {
        icon: ShieldCheckIcon,
        title: 'One compliant global entry model',
        description: 'Structured IOR execution across 42 countries without local entity setup.',
      },
      {
        icon: MapPinIcon,
        title: 'Regional control and phased release',
        description: 'A central consolidation hub with coordinated rollout across staged markets.',
      },
      {
        icon: ComputerDesktopIcon,
        title: 'Installation-ready precision',
        description: 'Every asset tagged, mapped, and delivered to the exact room and location required.',
      },
      {
        icon: WrenchScrewdriverIcon,
        title: 'End-to-end on-site execution',
        description: 'Certified L1 rack and stack, secure removal, and replacement of legacy equipment.',
      },
    ],
    results: ['35 direct deliveries', '7 staged markets', '100% IOR compliant'],
    accentColor: 'from-blue-500 to-blue-700',
    icon: GlobeAltIcon,
  },
  {
    id: 'apac-itam',
    title: 'APAC ITAM Fulfilment for a Remote Work Platform',
    client: 'A global digital platform managing remote teams',
    challenge:
      'The programme needed structured hardware fulfilment across APAC with local staging precision, real-time visibility, and scalable lifecycle control.',
    deliverables: [
      {
        icon: TruckIcon,
        title: 'Local fulfilment',
        description: 'Fast, precise staging and dispatch with certified repair support where the end users were based.',
      },
      {
        icon: BuildingOffice2Icon,
        title: 'Singapore regional hub',
        description: 'Seamless redistribution with compliant IOR/EOR and SLA-backed delivery into non-hub markets.',
      },
      {
        icon: EyeIcon,
        title: 'Live ITAM visibility',
        description: 'Real-time control with role-based access, sub-location tracking, and integrated ticketing.',
      },
      {
        icon: ArrowPathIcon,
        title: 'End-to-end lifecycle',
        description: 'Returns, repair, and re-installation were connected into one supportable operating flow.',
      },
    ],
    results: ['Local fulfilment hubs', 'IOR/EOR for non-hub markets'],
    accentColor: 'from-cyan-500 to-cyan-700',
    icon: ComputerDesktopIcon,
  },
  {
    id: 'telecom-ior-eor',
    title: 'Southeast Asia IOR/EOR Rollout for a Telecom MNC',
    client: 'A multinational telecommunications company',
    challenge:
      'The customer needed to deploy ICT equipment across multiple Southeast Asian countries while protecting compliance, timing, and equipment integrity.',
    deliverables: [
      {
        icon: DocumentCheckIcon,
        title: 'ICT logistics planning',
        description: 'Cross-country coordination for hardware movement, paperwork, and local execution sequencing.',
      },
      {
        icon: ShieldCheckIcon,
        title: 'IOR and EOR execution',
        description: 'TrueLog acted as both the Importer and Exporter of Record while managing the full document set.',
      },
      {
        icon: GlobeAltIcon,
        title: 'Customs clearance and compliance',
        description: 'Full customs oversight with real-time visibility into exceptions and status.',
      },
      {
        icon: ClockIcon,
        title: 'Timely delivery and secure handling',
        description: 'Delivery stayed aligned to project milestones while keeping sensitive hardware protected.',
      },
    ],
    results: ['100% IOR/EOR compliance', 'On-time delivery', 'Damage-free handling'],
    accentColor: 'from-indigo-500 to-indigo-700',
    icon: SignalIcon,
  },
];

const CaseStudies: React.FC = () => {
  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Case Studies"
        title="Execution proof from live global hardware programmes."
        highlight="Execution proof"
        description="These case studies show how TrueLog handles compliance, routing, warehouse control, deployment readiness, and lifecycle execution in environments where failure is visible and expensive."
        heroIcon={BuildingOffice2Icon}
        accentIcon={GlobeAltIcon}
        actions={[
          { href: '/contact-us', label: 'Talk to experts' },
          { href: '/services', label: 'Explore services', variant: 'secondary' },
        ]}
        badges={[
          'Multi-country rollout execution',
          'IOR/EOR programme delivery',
          'ITAM and fulfilment control',
        ]}
        metrics={[
          { value: `${caseStudies.length}`, label: 'Featured case studies' },
          { value: '42', label: 'Countries in largest rollout' },
          { value: '100%', label: 'IOR/EOR compliance in featured telecom programme' },
        ]}
        panelEyebrow="Featured outcomes"
        panelTitle="Real programmes, not theoretical models"
        panelDescription="Each case study captures a different version of the same challenge: moving hardware globally without losing timing, compliance, or operational control."
        panelItems={caseStudies.slice(0, 3).map((study) => ({
          icon: study.icon,
          title: study.title,
          description: study.challenge,
        }))}
      />

      <section className="bg-white py-20">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Case Studies"
            title="A closer look at how TrueLog delivered"
            description="Each example below shows the customer challenge, the operating response, and the measurable outcome that came from a more structured execution model."
          />

          <div className="space-y-8">
            {caseStudies.map((study, index) => (
              <motion.article
                key={study.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.75, delay: index * 0.06 }}
                className="overflow-hidden rounded-[34px] border border-slate-200/80 bg-white shadow-[0_28px_70px_-42px_rgba(15,23,42,0.28)]"
              >
                <div className={`bg-gradient-to-r ${study.accentColor} p-7 text-white`}>
                  <div className="flex items-start gap-4">
                    <div className="inline-flex rounded-2xl bg-white/15 p-3">
                      <study.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/80">{study.client}</p>
                      <h2 className="mt-2 text-3xl font-bold tracking-tight text-white">{study.title}</h2>
                    </div>
                  </div>
                </div>

                <div className="grid gap-8 p-7 lg:grid-cols-[0.9fr_1.1fr]">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Challenge</p>
                    <p className="mt-3 text-sm leading-8 text-slate-600">{study.challenge}</p>

                    <div className="mt-6 rounded-[24px] border border-slate-200/80 bg-slate-50 p-5">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2447d8]">Results</p>
                      <div className="mt-4 flex flex-wrap gap-3">
                        {study.results.map((result) => (
                          <span
                            key={result}
                            className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            {result}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    {study.deliverables.map((item) => (
                      <div
                        key={item.title}
                        className="rounded-[26px] border border-slate-200/80 bg-slate-50 p-5"
                      >
                        <div className="inline-flex rounded-2xl bg-white p-3 text-[#2447d8] shadow-[0_16px_35px_-28px_rgba(15,23,42,0.3)]">
                          <item.icon className="h-5 w-5" />
                        </div>
                        <h3 className="mt-4 text-lg font-semibold text-slate-950">{item.title}</h3>
                        <p className="mt-2 text-sm leading-7 text-slate-600">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <PageCta
        title="Need the same level of control in your own rollout?"
        description="If your programme has similar pressure around compliance, deployment timing, or multi-country coordination, we can shape the execution model around that environment."
        pills={['Global rollout delivery', 'IOR/EOR execution', 'ITAM and fulfilment support']}
        secondaryHref="/resources"
        secondaryLabel="Explore resources"
      />
    </div>
  );
};

export default CaseStudies;
